import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";

/** Listing IDs the model mentions inline, e.g. `zillow:39258095`. */
const LISTING_ID_SPLIT = /\b((?:zillow|realtor):\d+)\b/;
const LISTING_ID = /^(?:zillow|realtor):\d+$/;

/** Turn bare listing IDs inside a string into links to the detail page. */
export function linkifyPlain(text: string): React.ReactNode {
  const parts = text.split(LISTING_ID_SPLIT);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    LISTING_ID.test(part) ? (
      <Link key={i} to={`/listings/${part}`} className="md-listing">
        {part}
      </Link>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

/**
 * Same, but over rendered markdown children: only direct string children are
 * split — nested elements are rendered by their own component, which linkifies
 * its own strings.
 */
function linkify(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) =>
    typeof child === "string" ? linkifyPlain(child) : child,
  );
}

function heading(level: number) {
  const Tag = `h${level}` as "h1";
  return function Heading({ children }: { children?: React.ReactNode }) {
    return <Tag className={`md-h md-h${level}`}>{linkify(children)}</Tag>;
  };
}

const COMPONENTS: Components = {
  p: ({ children }) => <p className="md-p">{linkify(children)}</p>,
  h1: heading(1),
  h2: heading(2),
  h3: heading(3),
  h4: heading(4),
  h5: heading(5),
  h6: heading(6),
  ul: ({ children }) => <ul className="md-list">{children}</ul>,
  ol: ({ children }) => <ol className="md-list">{children}</ol>,
  li: ({ children }) => <li className="md-li">{linkify(children)}</li>,
  strong: ({ children }) => <strong className="md-strong">{linkify(children)}</strong>,
  em: ({ children }) => <em>{linkify(children)}</em>,
  del: ({ children }) => <del className="md-del">{linkify(children)}</del>,
  blockquote: ({ children }) => <blockquote className="md-quote">{children}</blockquote>,
  hr: () => <hr className="md-hr" />,
  // A single `code` component covers both inline and fenced code; the fenced
  // case is de-styled by `.md-pre .md-code` so we don't need to detect it here.
  code: ({ children }) => <code className="md-code">{children}</code>,
  pre: ({ children }) => <pre className="md-pre">{children}</pre>,
  // Tables can be wider than the bubble (especially in the floating panel),
  // so they scroll inside their own box rather than stretching it.
  table: ({ children }) => (
    <div className="md-table-wrap">
      <table className="md-table">{children}</table>
    </div>
  ),
  // `style` carries the column alignment from the `| ---: |` row.
  th: ({ children, style }) => (
    <th className="md-th" style={style}>
      {linkify(children)}
    </th>
  ),
  td: ({ children, style }) => (
    <td className="md-td" style={style}>
      {linkify(children)}
    </td>
  ),
  a: ({ href, children }) => {
    const url = href ?? "";
    if (url.startsWith("/")) {
      return (
        <Link to={url} className="md-a">
          {children}
        </Link>
      );
    }
    return (
      <a className="md-a" href={url} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

/** Renders an assistant message: GitHub-flavored markdown, dark-theme styled. */
export default function ChatMarkdown({ text, compact }: { text: string; compact?: boolean }) {
  return (
    <div className={compact ? "md md--compact" : "md"}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** Flattens a stored chat message (string, text, or content blocks) to text. */
export function messageText(m: {
  content?: unknown;
  text?: string;
}): string {
  if (m.text) return m.text;
  if (typeof m.content === "string") return m.content;
  if (Array.isArray(m.content)) {
    return (m.content as Array<{ type?: string; text?: string }>)
      .filter((b) => b.type === "text")
      .map((b) => b.text || "")
      .join("\n");
  }
  return "";
}
